import {
  OfflineSessionState,
  TaxDocumentType,
  TaxJournalDocument,
  TaxJournalReplacement,
  TransactionsRegistrarStateResponse,
  hashTaxDocument,
  parseOrderDateTime,
  parseXml,
  renumberOfflineDocument,
} from 'tax-register-core';
import { isFakePrro } from './fake-prro';

// Recovery mode of the tax registers (the tax_recovery_mode setting): fiscalization never stops
// the point of sale. Offline work starts and continues without the package's checks (its unsafe
// operations), and the upload repairs what the server rejects. This file holds the document
// logic of that upload; TaxService runs it.

// Session credentials for an offline session started without any in force. Fiscal numbers made
// with them are knowingly invalid; the upload renumbers those documents with the server's.
export const RECOVERY_OFFLINE_SESSION: OfflineSessionState = {
  OfflineSessionId: '0',
  OfflineSeed: '0',
};

// Where the next offline document goes on the server: from its state and its last document.
export interface RecoveryPosition {
  nextLocalNum: number; // NextLocalNum
  offlineNextLocalNum: number; // OfflineNextLocalNum; 1 = no session open
  offlineSessionId: string;
  offlineSeed: string;
  previousDocumentHash: string | null; // SHA-256 of the server's last document
  // Date and time of the server's last document: the server rejects a document dated before it.
  previousDocumentTime?: DocumentTime | null;
  shiftOpened?: boolean; // ShiftState is 1; absent when not known
}

// ORDERDATE (DDMMYYYY) and ORDERTIME (HHMMSS) of a document, as written.
export interface DocumentTime {
  orderDate: string;
  orderTime: string;
}

// The current offline session as the upload sees it.
export interface RecoverySequence {
  // Documents the server does not have, in sending order.
  send: TaxJournalDocument[];
  // Not sent.
  revoked: TaxJournalDocument[];
  // Not sent, and removed from the journal.
  duplicates: TaxJournalDocument[];
  // Not sent: the server has them (sent before, their answer possibly lost).
  onServer: TaxJournalDocument[];
  // The server's end of this session when the server closed it but the journal lacks the end
  // (its response was lost).
  serverSessionEnd: string | null;
}

// The issuer values of a document header, as a new document of the register gets them: TIN,
// IPN, ORGNM, POINTNM, POINTADDR, CASHDESKNUM and CASHREGISTERNUM.
export interface RecoveryIssuer {
  tin?: string;
  ipn?: string;
  organizationName?: string;
  pointName?: string;
  pointAddress?: string;
  cashDeskNum?: string | number;
  cashRegisterNum?: string | number;
}

interface DocumentHead {
  [name: string]: string | undefined;
  UID?: string;
  DOCTYPE?: string;
  DOCSUBTYPE?: string;
  PREVDOCHASH?: string;
}

// The issuer elements of CHECKHEAD and ZREPHEAD (check01.xsd, zrep01.xsd): the value each takes,
// the elements it follows (where a missing one goes) and whether the document builder escapes
// its text.
const ISSUER_ELEMENTS: Array<{
  name: string;
  value: keyof RecoveryIssuer;
  after: string[];
  escaped: boolean;
}> = [
  { name: 'TIN', value: 'tin', after: ['UID'], escaped: false },
  { name: 'IPN', value: 'ipn', after: ['TIN'], escaped: false },
  { name: 'ORGNM', value: 'organizationName', after: ['IPN', 'TIN'], escaped: true },
  { name: 'POINTNM', value: 'pointName', after: ['ORGNM'], escaped: true },
  { name: 'POINTADDR', value: 'pointAddress', after: ['POINTNM'], escaped: true },
  { name: 'CASHDESKNUM', value: 'cashDeskNum', after: ['ORDERNUM'], escaped: false },
  {
    name: 'CASHREGISTERNUM',
    value: 'cashRegisterNum',
    after: ['CASHDESKNUM'],
    escaped: false,
  },
];

// As the document builder escapes text: ʼ is written as &apos; too.
const XML_ESCAPES: Record<string, string> = {
  '<': '&lt;',
  '>': '&gt;',
  '&': '&amp;',
  "'": '&apos;',
  'ʼ': '&apos;',
  '"': '&quot;',
};

// The current session is the offline documents after the last saved OfflineEnd. Sending resumes
// after the last of them the server has: one whose XML a server document repeats, or one a
// server document names in PREVDOCHASH (that document follows it on the server; when it is an
// OfflineEnd, the server closed the session). Of the rest, revoked documents are not sent, and
// neither are duplicates: documents with the UID, DOCTYPE and DOCSUBTYPE of an earlier session
// document or of a server document — the offline copy of a check the server registered online
// included. serverDocumentsXml are the server's documents as sent (OriginalXml): its last one
// and, while its session is open, the one before that session.
export function selectRecoveryDocuments(
  documents: TaxJournalDocument[],
  serverDocumentsXml: Array<string | null | undefined>,
): RecoverySequence {
  const session = getSessionDocuments(documents);
  const server = serverDocumentsXml.filter(hasText).map((xml) => ({
    xml,
    head: readHead(xml),
    hash: hashTaxDocument(xml),
  }));

  let resume = -1;
  let serverSessionEnd: string | null = null;
  session.forEach((document, position) => {
    if (document.Revoked) {
      return;
    }
    const hash = hashTaxDocument(document.xmlCheck);
    if (server.some((item) => item.hash === hash)) {
      resume = Math.max(resume, position);
    }
    const successor = server.find(
      (item) => text(item.head.PREVDOCHASH).toLowerCase() === hash,
    );
    if (successor) {
      resume = Math.max(resume, position);
      if (Number(successor.head.DOCTYPE) === TaxDocumentType.OFFLINE_END) {
        serverSessionEnd = successor.xml;
      }
    }
  });

  const seen = new Set(
    server.map((item) => operationKey(item.head)).filter(Boolean),
  );
  const sequence: RecoverySequence = {
    send: [],
    revoked: [],
    duplicates: [],
    onServer: [],
    serverSessionEnd,
  };
  session.forEach((document, position) => {
    const key = operationKey(readHead(document.xmlCheck));
    if (position <= resume) {
      if (!document.Revoked) {
        sequence.onServer.push(document);
        if (key) {
          seen.add(key);
        }
      }
      return;
    }

    if (document.Revoked) {
      sequence.revoked.push(document);
    } else if (key && seen.has(key)) {
      sequence.duplicates.push(document);
    } else {
      if (key) {
        seen.add(key);
      }
      sequence.send.push(document);
    }
  });
  return sequence;
}

// The journal changes that make the documents to send continue from the server's position, and
// those documents in sending order.
// - Duplicates are removed.
// - While the server's session is open, the documents continue it, so the session's own
//   OfflineBegin is removed. With no session open there, the first document must be an
//   OfflineBegin.
// - A shift the server has open is not opened again: a shift opening that starts the documents
//   (after their OfflineBegin) is removed. A fake context's session always starts with one.
// - Each document's header gets the issuer's values when one is given (replaceDocumentIssuer).
// - A document dated before the document it follows (the server's last one, for the first)
//   takes that document's date and time (clampDocumentTime): the server rejects a document
//   dated before its previous one, so documents made while the server registered later ones (a
//   fake session, another terminal of the register) could never be sent as dated.
// - Each document gets the next global and session numbers, the server's session credentials,
//   PREVDOCHASH of the document before it and a new control number, which includes the date and
//   time; UID, cashier and body stay (renumberOfflineDocument).
// - The first renumbered document puts the server's credentials in force, so the documents
//   created afterwards, and the session end, use them.
// retimed: how many documents took a later date and time.
export function planRecoveryRebuild(
  sequence: RecoverySequence,
  position: RecoveryPosition,
  issuer?: RecoveryIssuer,
): { changes: TaxJournalReplacement[]; documents: string[]; retimed: number } {
  if (sequence.serverSessionEnd) {
    throw new Error(
      'Сервер уже завершив цю офлайн сесію: документи, створені після неї, потребують нової сесії.',
    );
  }

  const changes: TaxJournalReplacement[] = sequence.duplicates.map((document) => ({
    xml: document.xmlCheck,
    replacement: null,
  }));
  let send = sequence.send;
  const startsWithBegin = send[0]?.CheckDocType === 'OfflineBegin';
  if (position.offlineNextLocalNum > 1) {
    if (startsWithBegin) {
      changes.push({ xml: send[0].xmlCheck, replacement: null });
      send = send.slice(1);
    }
  } else if (send.length && !startsWithBegin) {
    throw new Error(
      'На сервері немає відкритої офлайн сесії, а першим документом для надсилання є не «Початок офлайн сесії».',
    );
  }
  const shiftIndex = send[0]?.CheckDocType === 'OfflineBegin' ? 1 : 0;
  if (position.shiftOpened === true && send[shiftIndex]?.CheckDocType === 'OpenShift') {
    changes.push({ xml: send[shiftIndex].xmlCheck, replacement: null });
    send = send.filter((_, index) => index !== shiftIndex);
  }

  const documents: string[] = [];
  let previousDocumentHash = position.previousDocumentHash;
  let previousDocumentTime = position.previousDocumentTime ?? null;
  let retimed = 0;
  send.forEach((document, index) => {
    const withIssuer = issuer
      ? replaceDocumentIssuer(document.xmlCheck, issuer)
      : document.xmlCheck;
    const prepared = clampDocumentTime(withIssuer, previousDocumentTime);
    if (prepared !== withIssuer) {
      retimed++;
    }
    const renumbered = renumberAtPosition(
      prepared,
      position,
      index,
      previousDocumentHash,
    );
    // A document the rebuild leaves as it was keeps its bytes, so its hash: renumbering moves
    // the number elements, which would change the next document's PREVDOCHASH and so its
    // fiscal number.
    const xml =
      prepared === document.xmlCheck && isNumberedAs(document.xmlCheck, renumbered)
        ? document.xmlCheck
        : renumbered;
    changes.push({
      xml: document.xmlCheck,
      replacement: xml,
      offlineCredentials:
        index === 0
          ? {
              OfflineSessionId: position.offlineSessionId,
              OfflineSeed: position.offlineSeed,
            }
          : undefined,
    });
    documents.push(xml);
    previousDocumentHash = hashTaxDocument(xml);
    previousDocumentTime = readDocumentTime(xml) ?? previousDocumentTime;
  });
  return { changes, documents, retimed };
}

// The document renumbered as the next one on the server, `offset` documents later.
export function renumberAtPosition(
  xml: string,
  position: RecoveryPosition,
  offset = 0,
  previousDocumentHash = position.previousDocumentHash,
): string {
  return renumberOfflineDocument(xml, {
    orderNumber: position.nextLocalNum + offset,
    sessionId: position.offlineSessionId,
    seed: position.offlineSeed,
    localNum: position.offlineNextLocalNum + offset,
    previousDocumentHash,
  });
}

// The issuer a new document of the register gets (the service's createDocumentHead with
// TaxService's fallback): the values of the register's TaxObject — the server's, as the upload
// reads it with the register's state — and, where it has none, those of the tax-register config.
export function getRecoveryIssuer(
  taxRegisterFiscalNumber: string | number,
  taxObject: TransactionsRegistrarStateResponse['TaxObject'] | null | undefined,
  config: RecoveryIssuer,
): RecoveryIssuer {
  const registrar = taxObject?.TransactionsRegistrars?.find(
    (item) => text(item?.NumFiscal) === text(taxRegisterFiscalNumber),
  );
  return {
    tin: text(taxObject?.Tin) || text(config.tin),
    ipn: text(taxObject?.Ipn) || text(config.ipn),
    organizationName: text(taxObject?.OrgName) || text(config.organizationName),
    pointName: text(taxObject?.Name) || text(config.pointName),
    pointAddress: text(taxObject?.Address) || text(config.pointAddress),
    cashDeskNum: text(registrar?.NumLocal) || text(config.cashDeskNum),
    cashRegisterNum: text(taxRegisterFiscalNumber),
  };
}

// The document with the issuer's values in its header (CHECKHEAD or ZREPHEAD), written as the
// document builder writes them. A value already there stays as written, and so does one the
// issuer lacks: a document whose header is right comes back byte-identical. Only the header
// changes and its numbers are not updated: a changed document must be renumbered.
export function replaceDocumentIssuer(xml: string, issuer: RecoveryIssuer): string {
  const head = readHead(xml);
  let result = xml;
  for (const element of ISSUER_ELEMENTS) {
    const value = text(issuer?.[element.value]);
    if (!value || isWritten(head[element.name], value, element.name)) {
      continue;
    }
    result = writeHeadElement(
      result,
      element.name,
      element.escaped ? escapeXml(value) : value,
      element.after,
    );
  }
  return result;
}

// The offline document's fiscal number recomputed for its header as it now is: the same
// session, numbers and PREVDOCHASH, under the session's seed (after replaceDocumentIssuer).
export function recomputeOfflineNumber(xml: string, seed: string | number): string {
  const head = readHead(xml);
  const [sessionId, localNum] = text(head.ORDERTAXNUM).split('.');
  return renumberOfflineDocument(xml, {
    orderNumber: Number(head.ORDERNUM),
    sessionId,
    seed,
    localNum: Number(localNum),
    previousDocumentHash: text(head.PREVDOCHASH) || null,
  });
}

// Whether a document's header lacks the issuer's values, so the document must be rebuilt.
export function needsIssuerUpdate(
  documents: TaxJournalDocument[],
  issuer: RecoveryIssuer,
): boolean {
  return documents.some(
    (document) => replaceDocumentIssuer(document.xmlCheck, issuer) !== document.xmlCheck,
  );
}

// The document's ORDERDATE and ORDERTIME, as written; null when its header lacks them.
export function readDocumentTime(xml: string | null | undefined): DocumentTime | null {
  if (!hasText(xml)) {
    return null;
  }
  const head = readHead(xml);
  return hasText(head.ORDERDATE) && hasText(head.ORDERTIME)
    ? { orderDate: text(head.ORDERDATE), orderTime: text(head.ORDERTIME) }
    : null;
}

// The document dated no earlier than the previous one: one dated before it takes its date and
// time, written in full (DDMMYYYY, HHMMSS). Equal times are allowed. Only the header changes and
// its numbers are not updated: a changed document must be renumbered.
export function clampDocumentTime(
  xml: string,
  previous: DocumentTime | null | undefined,
): string {
  const time = readDocumentTime(xml);
  if (!previous || !time || documentTimeKey(time) >= documentTimeKey(previous)) {
    return xml;
  }
  const parts = parseOrderDateTime(previous.orderDate, previous.orderTime);
  const dated = writeHeadElement(
    xml,
    'ORDERDATE',
    `${parts.day}${parts.month}${parts.year}`,
    ['POINTADDR', 'POINTNM', 'ORGNM'],
  );
  return writeHeadElement(
    dated,
    'ORDERTIME',
    `${parts.hours}${parts.minutes}${parts.seconds}`,
    ['ORDERDATE'],
  );
}

// Whether a document to send is dated before the document it follows (the server's last one,
// for the first), so the server would reject it and the documents must be rebuilt.
export function needsTimeUpdate(
  documents: TaxJournalDocument[],
  previous: DocumentTime | null | undefined,
): boolean {
  let latest = previous ?? null;
  for (const document of documents) {
    const time = readDocumentTime(document.xmlCheck);
    if (!time) {
      continue;
    }
    if (latest && documentTimeKey(time) < documentTimeKey(latest)) {
      return true;
    }
    latest = time;
  }
  return false;
}

// Whether the documents to send were numbered under another offline session than the server's:
// made-up credentials (a fakePrro session, recovery mode's stand-ins) or stale ones. The server
// would reject every one of them, so the upload converts the session to the server's numbering
// (planRecoveryRebuild) before sending anything. A session under the server's credentials is sent
// as saved: its fiscal numbers are on the customers' receipts.
export function needsConversion(
  sequence: RecoverySequence,
  position: RecoveryPosition,
): boolean {
  const first = sequence.send[0];
  return (
    !!first &&
    text(first.NumFiscal).split('.')[0] !== text(position.offlineSessionId)
  );
}

// Whether only the recovery upload can send the open offline session: it was started with
// made-up credentials (a fakePrro session, or recovery mode's stand-ins), which its OfflineBegin
// carries, or the upload has begun rebuilding it, so a renumbered document carries the server's.
// The normal upload sends documents as they are.
export function needsRecoveryUpload(documents: TaxJournalDocument[]): boolean {
  for (let index = documents.length - 1; index >= 0; index--) {
    const document = documents[index];
    if (
      document.CheckDocType === 'OfflineEnd' ||
      !isOfflineFiscalNumber(document.NumFiscal)
    ) {
      return false;
    }
    if (document.offlineCredentials) {
      return true;
    }
    if (document.CheckDocType === 'OfflineBegin') {
      return false;
    }
  }
  return false;
}

// Whether the register works in recovery mode without the setting: a fakePrro context with
// documents, or an open offline session only the recovery upload can send.
export function needsRecoveryMode(
  registerState: unknown,
  documents: TaxJournalDocument[],
): boolean {
  return (
    (isFakePrro(registerState) && documents.length > 0) ||
    needsRecoveryUpload(documents)
  );
}

// The documents of the open offline session (after the last OfflineEnd or online document):
// saved offline, and not known to be on the tax server.
export function countOpenOfflineSessionDocuments(
  documents: TaxJournalDocument[],
): number {
  let count = 0;
  for (let index = documents.length - 1; index >= 0; index--) {
    const document = documents[index];
    if (
      document.CheckDocType === 'OfflineEnd' ||
      !isOfflineFiscalNumber(document.NumFiscal)
    ) {
      break;
    }
    count++;
  }
  return count;
}

// A check (DOCTYPE below 100: sales, returns, service deposits and issues) or a Z-report. The
// server times an offline session by its last such document and refuses to end a session without
// one ("Пакет порожній"); OfflineBegin, OfflineEnd and shift openings and closings do not count.
export function isFinancialDocument(xml: string | null | undefined): boolean {
  if (!hasText(xml)) {
    return false;
  }
  const document = parseXml<{
    CHECK?: { CHECKHEAD?: DocumentHead };
    ZREP?: unknown;
  }>(xml, { keepText: true });
  if (document?.ZREP) {
    return true;
  }
  if (!document?.CHECK) {
    return false;
  }
  const docType = document.CHECK.CHECKHEAD?.DOCTYPE;
  return !hasText(docType) || Number(docType) < TaxDocumentType.OPEN_SHIFT;
}

// The offline documents after the last saved OfflineEnd, in journal order.
function getSessionDocuments(documents: TaxJournalDocument[]): TaxJournalDocument[] {
  let start = 0;
  for (let index = documents.length - 1; index >= 0; index--) {
    if (documents[index].CheckDocType === 'OfflineEnd') {
      start = index + 1;
      break;
    }
  }
  return documents
    .slice(start)
    .filter((document) => isOfflineFiscalNumber(document.NumFiscal));
}

// <OfflineSessionId>.<number in the session>.<control number>
function isOfflineFiscalNumber(value: unknown): boolean {
  const parts = text(value).split('.');
  return parts.length === 3 && Number.isInteger(Number(parts[1]));
}

// Identity of a fiscal operation for the duplicate check; null without a UID.
function operationKey(head: DocumentHead): string | null {
  const uid = text(head?.UID).toLowerCase();
  if (!uid) {
    return null;
  }
  const docType = hasText(head.DOCTYPE) ? Number(head.DOCTYPE) : 'ZREP';
  const docSubType = hasText(head.DOCSUBTYPE) ? Number(head.DOCSUBTYPE) : 0;
  return `${uid}|${docType}|${docSubType}`;
}

function readHead(xml: string): DocumentHead {
  const document = parseXml<{
    CHECK?: { CHECKHEAD?: DocumentHead };
    ZREP?: { ZREPHEAD?: DocumentHead };
  }>(xml, { keepText: true });
  return document?.CHECK?.CHECKHEAD || document?.ZREP?.ZREPHEAD || {};
}

// YYYYMMDDHHMMSS, for comparing document times; values that lost a leading zero are restored.
function documentTimeKey(time: DocumentTime): string {
  const parts = parseOrderDateTime(time.orderDate, time.orderTime);
  return `${parts.year}${parts.month}${parts.day}${parts.hours}${parts.minutes}${parts.seconds}`;
}

// Whether two versions of a document carry the same numbers.
function isNumberedAs(xml: string, other: string): boolean {
  const head = readHead(xml);
  const otherHead = readHead(other);
  return ['ORDERNUM', 'ORDERTAXNUM', 'OFFLINE', 'PREVDOCHASH'].every(
    (name) => text(head[name]) === text(otherHead[name]),
  );
}

// Whether the header's value (as read) is the issuer's: a number by its value, text regardless
// of how an apostrophe was written.
function isWritten(written: unknown, value: string, name: string): boolean {
  if (name === 'CASHDESKNUM') {
    return hasText(written) && Number(text(written)) === Number(value);
  }
  const apostrophes = (item: string) => item.replace(/ʼ/g, "'");
  return apostrophes(text(written)) === apostrophes(value);
}

function escapeXml(value: string): string {
  return value.replace(/[<>&'"ʼ]/g, (char) => XML_ESCAPES[char]);
}

// Writes an element's text in the header, or adds the element after the first element of
// `after` the header has, on its own line as the builder lays it out.
function writeHeadElement(
  xml: string,
  name: string,
  value: string,
  after: string[],
): string {
  const headEnd = Math.max(xml.indexOf('</CHECKHEAD>'), xml.indexOf('</ZREPHEAD>'));
  if (headEnd < 0) {
    throw new Error('Tax document CHECKHEAD or ZREPHEAD is missing.');
  }

  let head = xml.slice(0, headEnd);
  const element = `<${name}>${value}</${name}>`;
  const written = new RegExp(`<${name}>[^<]*</${name}>|<${name}\\s*/>`);
  if (written.test(head)) {
    head = head.replace(written, () => element);
  } else {
    const anchor = after
      .map((previous) => `</${previous}>`)
      .find((end) => head.includes(end));
    if (!anchor) {
      throw new Error(`Tax document header has no place for ${name}.`);
    }
    let at = head.indexOf(anchor) + anchor.length;
    const lineBreak = /^\r?\n/.exec(head.slice(at))?.[0] ?? '';
    at += lineBreak.length;
    head = head.slice(0, at) + element + lineBreak + head.slice(at);
  }
  return head + xml.slice(headEnd);
}

function hasText(value: unknown): value is string {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function text(value: unknown): string {
  return hasText(value) ? String(value).trim() : '';
}
